/**
 * Resident "Ask a Question" intake (ask-a-question-faq.md §5.1 / feature ledger §1). A variant of
 * the concern IntakePage: topic picker (Kind=question categories) + question text + optional
 * contact + CAPTCHA — but no location/attachments (a question is text, not a site report). As the
 * resident types the question, a debounced GET /public/faq/search shows related published FAQs —
 * "deflect before asking" (§5.1, promoted-to-MVP). Same anonymous-allowed, WCAG 2.1 AA, error
 * summary + tracking-code confirmation flow as the concern intake (♻️ engine).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { submitQuestion, searchFAQ, ApiError, type FAQView } from "../api/index.js";
import { ContactSection, type ContactValue } from "../components/ContactSection.js";
import { CaptchaField } from "../components/CaptchaField.js";
import { useCaptcha } from "../components/useCaptcha.js";
import { Field } from "../components/Field.js";
import { Button } from "../components/Button.js";
import { ErrorSummary, type FieldErrorRef } from "../components/ErrorSummary.js";
import { SpinnerIcon, SearchIcon } from "../components/icons.js";
import { TownHeader } from "../components/TownHeader.js";
import { ResidentThemeRoot } from "../components/ResidentThemeRoot.js";
import { useDocumentTitle, pageTitle } from "../components/useDocumentTitle.js";
import { useEmbedClose } from "../components/useEmbedClose.js";
import { useTheme } from "../theme/ThemeProvider.js";

const MAX_QUESTION_LEN = 1000;
const MIN_QUESTION_FOR_SEARCH = 4;

const ID = {
  topic: "field-ask-topic",
  question: "field-ask-question",
  email: "field-ask-contact-email",
  captcha: "field-ask-captcha",
};

interface AskPageProps {
  embed?: boolean;
}

/** Debounce a fast-changing value (typeahead search box). */
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export function AskPage({ embed = false }: AskPageProps): JSX.Element {
  const navigate = useNavigate();
  const { config, questionTopics: topics } = useTheme();
  useDocumentTitle(pageTitle("Ask a Question", config.townName));
  useEmbedClose(embed);

  const [topic, setTopic] = useState<string | null>(null);
  const [question, setQuestion] = useState("");
  const [contact, setContact] = useState<ContactValue>({ name: "", email: "", phone: "" });
  const captcha = useCaptcha();

  const [summaryErrors, setSummaryErrors] = useState<FieldErrorRef[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitState, setSubmitState] = useState<"idle" | "submitting" | "error">("idle");
  const [submitErrorMsg, setSubmitErrorMsg] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // Related-FAQ typeahead (§5.1 "deflect before asking") — debounced search over the same public
  // FAQ index, scoped to the chosen topic once one is picked.
  const debouncedQuestion = useDebounced(question, 350);
  const [related, setRelated] = useState<FAQView[]>([]);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [dismissedRelated, setDismissedRelated] = useState(false);

  useEffect(() => {
    setDismissedRelated(false);
  }, [debouncedQuestion]);

  useEffect(() => {
    const q = debouncedQuestion.trim();
    if (q.length < MIN_QUESTION_FOR_SEARCH) {
      setRelated([]);
      return;
    }
    let cancelled = false;
    setRelatedLoading(true);
    searchFAQ(q, topic ?? undefined)
      .then((faqs) => {
        if (!cancelled) setRelated(faqs.slice(0, 3));
      })
      .catch(() => {
        if (!cancelled) setRelated([]);
      })
      .finally(() => {
        if (!cancelled) setRelatedLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuestion, topic]);

  const showRelated = !dismissedRelated && related.length > 0;

  const requiredMissing = !topic || !question.trim() || !captcha.passed;

  const helperForMissing = useMemo(() => {
    const missing: string[] = [];
    if (!topic) missing.push("a topic");
    if (!question.trim()) missing.push("your question");
    if (!captcha.passed) missing.push("the spam check");
    return missing;
  }, [topic, question, captcha.passed]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitState === "submitting") return;

    const nextField: Record<string, string> = {};
    const summary: FieldErrorRef[] = [];
    if (!topic) {
      nextField[ID.topic] = "Choose a topic.";
      summary.push({ targetId: ID.topic, message: "Choose a topic." });
    }
    if (!question.trim()) {
      nextField[ID.question] = "Enter your question.";
      summary.push({ targetId: ID.question, message: "Enter your question." });
    }
    const email = contact.email.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      nextField[ID.email] = "Enter a valid email, or leave contact blank.";
      summary.push({ targetId: ID.email, message: "Enter a valid email, or leave contact blank." });
    }
    if (!captcha.passed) {
      nextField[ID.captcha] = "Please complete the spam check.";
      summary.push({ targetId: ID.captcha, message: "Please complete the spam check." });
    }
    if (Object.keys(nextField).length) {
      setFieldErrors(nextField);
      setSummaryErrors(summary);
      return;
    }
    setFieldErrors({});
    setSummaryErrors([]);

    // Submit safety net: reuse the fresh inline-solved token (fast no-op) or re-challenge if expired.
    if (!(await captcha.ensureForSubmit())) {
      const msg = "We couldn't verify the spam check. Please try the puzzle again.";
      setSummaryErrors([{ targetId: ID.captcha, message: msg }]);
      return;
    }

    setSubmitState("submitting");
    setSubmitErrorMsg(null);
    try {
      const trimmedContact = {
        name: contact.name.trim() || undefined,
        email: contact.email.trim() || undefined,
        phone: contact.phone.trim() || undefined,
      };
      const hasContact = trimmedContact.name || trimmedContact.email || trimmedContact.phone;
      const result = await submitQuestion({
        topic: topic as string,
        question: question.trim(),
        ...(hasContact ? { reporterContact: trimmedContact } : {}),
      });
      const topicLabel = topics.find((t) => t.id === topic)?.label ?? topic;
      // Preserve `?embed=1&chrome=off` (and any other query params) across the navigate — App.tsx
      // derives `embed`/`chromeless` fresh from the URL on every route, so an embed submit that
      // dropped the search string here would silently lose chromeless/resize on /confirmation.
      navigate(`/confirmation${window.location.search}`, {
        state: {
          trackingCode: result.trackingCode,
          trackingUrl: result.trackingUrl,
          hasContact: Boolean(hasContact),
          email: trimmedContact.email,
          category: topicLabel,
          isQuestion: true,
        },
      });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : "Something went wrong submitting your question. Please try again.";
      setSubmitState("error");
      setSubmitErrorMsg(message);
    }
  };

  const submitLabel = submitState === "submitting" ? "Submitting…" : "Submit question";

  return (
    <ResidentThemeRoot className={`td-intake${embed ? " td-intake--embed" : ""}`}>
      <a href="#td-ask-form" className="td-skip-link">
        Skip to the question form
      </a>
      <TownHeader
        embed={embed}
        subcopy={`Ask ${config.townName} a question. We'll follow up — you don't need an account.`}
      />

      <main className="td-intake__main">
        <h1 className="td-intake__title">Ask a Question</h1>

        <ErrorSummary errors={summaryErrors} />

        {submitState === "error" && submitErrorMsg ? (
          <div className="td-alert td-alert--danger" role="alert">
            <p>{submitErrorMsg}</p>
            <Button variant="secondary" onClick={() => formRef.current?.requestSubmit()}>
              Try again
            </Button>
          </div>
        ) : null}

        <form
          id="td-ask-form"
          ref={formRef}
          className="td-form"
          noValidate
          onSubmit={onSubmit}
          aria-busy={submitState === "submitting"}
        >
          <Field id={ID.topic} label="Topic" required error={fieldErrors[ID.topic]}>
            {(aria) => (
              <select
                {...aria}
                className="td-select"
                value={topic ?? ""}
                onChange={(e) => setTopic(e.target.value || null)}
              >
                <option value="">Choose a topic…</option>
                {topics.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field
            id={ID.question}
            label="Your question"
            required
            helper="Ask in your own words — a staff member will follow up."
            error={fieldErrors[ID.question]}
          >
            {(aria) => (
              <>
                <textarea
                  {...aria}
                  className="td-input td-textarea"
                  rows={4}
                  maxLength={MAX_QUESTION_LEN}
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="What would you like to know?"
                />
                {question.length > 400 ? (
                  <span className="td-charcount" aria-live="polite">
                    {question.length} / {MAX_QUESTION_LEN}
                  </span>
                ) : null}
              </>
            )}
          </Field>

          {/* Related-FAQ typeahead (§5.1 "deflect before asking") — non-blocking suggestions, an
              aria-live region so screen-reader users hear when matches appear, and a dismiss
              control so it never traps focus or blocks the form. */}
          {relatedLoading || showRelated ? (
            <section
              className="td-related-faq"
              aria-labelledby="td-related-faq-title"
              aria-live="polite"
            >
              <h2 className="td-related-faq__title" id="td-related-faq-title">
                <SearchIcon width={16} height={16} aria-hidden="true" />
                {relatedLoading ? "Checking the FAQ…" : "This might already be answered"}
              </h2>
              {showRelated ? (
                <>
                  <ul className="td-related-faq__list">
                    {related.map((f) => (
                      <li key={f.id}>
                        <a href={`/faq/${f.id}`} target="_blank" rel="noopener noreferrer">
                          {f.question}
                        </a>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    className="td-related-faq__dismiss"
                    onClick={() => setDismissedRelated(true)}
                  >
                    None of these — keep writing my question
                  </button>
                </>
              ) : null}
            </section>
          ) : null}

          <ContactSection value={contact} onChange={setContact} emailError={fieldErrors[ID.email]} />

          <CaptchaField
            ref={captcha.containerRef}
            id={ID.captcha}
            status={captcha.status}
            error={captcha.error ?? undefined}
            passed={captcha.passed}
            onChange={(v) => {
              captcha.onChange(v);
              if (v && fieldErrors[ID.captcha]) {
                setFieldErrors((f) => {
                  const { [ID.captcha]: _drop, ...rest } = f;
                  return rest;
                });
              }
            }}
            onRetry={captcha.retry}
          />

          <div className="td-submit">
            <Button
              type="submit"
              fullWidth
              disabled={submitState === "submitting"}
              aria-disabled={requiredMissing || submitState === "submitting"}
            >
              {submitState === "submitting" ? (
                <>
                  <SpinnerIcon width={18} height={18} />
                  {submitLabel}
                </>
              ) : (
                submitLabel
              )}
            </Button>
            {requiredMissing && submitState !== "submitting" ? (
              <p className="td-submit__hint" aria-live="polite">
                Still needed: {helperForMissing.join(", ")}.
              </p>
            ) : null}
          </div>
        </form>
      </main>

      <footer className="td-footer">
        <span>
          Powered by{" "}
          <a href="https://towndesk.us" target="_blank" rel="noopener noreferrer">
            TownDesk
          </a>
        </span>
        {config.privacyUrl ? (
          <a href={config.privacyUrl} rel="noopener">
            Privacy
          </a>
        ) : null}
      </footer>
    </ResidentThemeRoot>
  );
}
